import type { AppDefinition } from '@/types/app';
import type { AppPlan } from './plan/planTypes';
import { baseTheme } from '@/guidance/templates/theme';
import { applyVibeTheme } from '@/guidance/vibes';

/** True when this project is a published web app — opened in the browser rather than
 *  rendered by any in-app runtime. Absent `runtime` means 'engine' (every older record). */
export function isWebApp(def: Pick<AppDefinition, 'runtime'>): boolean {
  return def?.runtime === 'webapp';
}

/** A unique, readable id: the slug plus a random suffix so two apps with the same name
 *  never share a storage namespace on the shared publishing origin. */
export function newWebAppId(appName: string): string {
  const slug = appName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'web-app';
  return `${slug}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Wrap a generated web app in an AppDefinition-shaped record so it flows through the
 * existing storage, listing, chat and sync paths untouched — the same trick
 * `buildMiniAppRecord` uses. `screens` and `schemas` stay empty: a web app declares no
 * engine data model and renders itself.
 *
 * The theme is metadata only (a web app is styled by its own CSS), kept consistent so
 * anything reading a project's theme for a listing color chip treats this record like any
 * other.
 */
export function buildWebAppRecord(input: {
  /**
   * The app's stable id. It is ALSO the localStorage namespace baked into the published
   * document (`window.__SADDLE_APP__`), so it must be generated once and never derived
   * again: re-deriving it at publish time gave the build and the re-publish two different
   * namespaces, and every re-publish silently wiped the user's saved data.
   */
  id: string;
  appName: string;
  goal: string;
  /** The app body the model produced — markup + <style> + <script>. */
  source: string;
  /** Public URL, once published. */
  webUrl?: string;
  vibe?: string | null;
  /** The plan this was built from. Kept on the record because publishing needs its
   *  `rules`: a capacity chosen in the wizard is decorative until it reaches the schema
   *  the server enforces, and publish happens long after the plan screen is gone. */
  plan?: AppPlan | null;
}): AppDefinition {
  const record: AppDefinition = {
    id: input.id,
    name: input.appName,
    description: input.goal,
    version: '1.0.0',
    theme: baseTheme,
    screens: [],
    schemas: [],
    navigation: { type: 'tabs', screens: [] },
    runtime: 'webapp',
    source: input.source,
    ...(input.webUrl ? { webUrl: input.webUrl } : {}),
    ...(input.plan ? { plan: input.plan } : {}),
  };
  return applyVibeTheme(record, input.vibe);
}
